import { useEffect, useRef } from 'react';
import { useApi, useApiHolder, configApiRef, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { platformToastApiRef } from '@platform/plugin-platform-react';

const POLL_INTERVAL = 30000; // 30s

interface PipelineState {
  projectId: string;
  componentName: string;
  status: string;
  ref: string;
}

/**
 * Surveille la dernière pipeline des composants de l'utilisateur (ceux qui
 * portent `gitlab.com/project-id`) et publie un toast quand son statut change.
 * Extrait de `PlatformNotifications` : le module qui appelle
 * `/api/gitlab-ci` est celui qui le surveille. Ne rend rien.
 */
export const PipelineWatcher = () => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  // Optionnelle : sans le socle, il n'y a personne à qui publier. Un `useApi`
  // lèverait et finirait en alerte de l'errorApi à chaque chargement.
  const toastApi = useApiHolder().get(platformToastApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const prevPipelinesRef = useRef<Map<string, PipelineState>>(new Map());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!toastApi) return undefined;

    const pollStatus = async () => {
      try {
        // Get user's owned components
        const identity = await identityApi.getBackstageIdentity();
        const userNormalized = identity.userEntityRef.replace(/^user:default\//, '');

        const componentsRes = await catalogApi.getEntities({
          filter: { kind: 'Component' },
          fields: ['metadata.name', 'metadata.annotations', 'spec.owner'],
        });

        // Filter owned components
        const myComponents = componentsRes.items.filter(c => {
          const owner = ((c.spec as any)?.owner ?? '').replace(/^(user|group):default\//, '');
          return owner.includes(userNormalized);
        });

        // Check pipelines
        const newPipelines = new Map<string, PipelineState>();
        await Promise.all(
          myComponents
            .filter(c => c.metadata.annotations?.['gitlab.com/project-id'])
            .map(async c => {
              const pid = c.metadata.annotations!['gitlab.com/project-id'];
              try {
                const r = await fetchApi.fetch(
                  `${backendUrl}/api/gitlab-ci/projects/${pid}/pipelines?per_page=1`,
                );
                if (r.ok) {
                  const data = await r.json();
                  if (data[0]) {
                    newPipelines.set(pid, {
                      projectId: pid,
                      componentName: c.metadata.name,
                      status: data[0].status,
                      ref: data[0].ref,
                    });
                  }
                }
              } catch {}
            }),
        );

        // Check for pipeline status changes
        if (initializedRef.current) {
          for (const [pid, current] of newPipelines) {
            const prev = prevPipelinesRef.current.get(pid);
            if (prev && prev.status !== current.status) {
              if (current.status === 'failed') {
                toastApi.post({
                  type: 'error',
                  title: `Pipeline en échec`,
                  message: `${current.componentName} (${current.ref}) — la pipeline a échoué`,
                });
              } else if (current.status === 'success' && prev.status !== 'success') {
                toastApi.post({
                  type: 'success',
                  title: `Pipeline réussie`,
                  message: `${current.componentName} (${current.ref}) — pipeline terminée avec succès`,
                });
              } else if (current.status === 'running' && prev.status !== 'running') {
                toastApi.post({
                  type: 'info',
                  title: `Pipeline en cours`,
                  message: `${current.componentName} (${current.ref}) — pipeline démarrée`,
                });
              }
            }
          }
        }
        prevPipelinesRef.current = newPipelines;
        initializedRef.current = true;
      } catch (e) {
        // Silent fail — notifications are best-effort
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [catalogApi, fetchApi, identityApi, toastApi, backendUrl]);

  return null;
};
